import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import confetti from "canvas-confetti";
import Iconify from "@/components/iconify";
import type { CMSHatTheme } from "@/types/cms-types";
import { celebrationButtonSx } from "../styles";

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const staggered = (delayMs: number, isActive: boolean) =>
  isActive
    ? {
        opacity: 0,
        animation: `${fadeSlideUp} 0.5s ease forwards`,
        animationDelay: `${delayMs}ms`,
      }
    : { opacity: 1 };

type HatPackSectionProps = {
  hatPack: CMSHatTheme;
  isActive: boolean;
  onAdvance: () => void;
  isLast: boolean;
};

export const HatPackSection = ({
  hatPack,
  isActive,
  onAdvance,
  isLast,
}: HatPackSectionProps) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isActive) return undefined;
    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    document.body.appendChild(canvas);
    const myConfetti = confetti.create(canvas, { resize: true });
    const t = setTimeout(() => {
      myConfetti({
        particleCount: 140,
        spread: 110,
        origin: { x: 0.5, y: 0.5 },
        disableForReducedMotion: true,
      });
    }, 700);
    return () => {
      clearTimeout(t);
      myConfetti.reset();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [isActive]);

  const goToHatPack = () => {
    onAdvance();
    navigate(`/ms/hats?theme=${encodeURIComponent(hatPack.id)}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        textAlign: "center",
        px: 1,
        width: "100%",
        maxWidth: 760,
      }}
    >
      <Stack spacing={1} sx={{ ...staggered(150, isActive), width: "100%" }}>
        <Typography
          sx={{
            fontFamily: "'Proxima Nova', sans-serif",
            fontWeight: 700,
            fontSize: { xs: 18, sm: 24 },
            lineHeight: { xs: "28px", sm: "36px" },
            color: "#fff",
            textTransform: "uppercase",
            letterSpacing: 0,
          }}
        >
          New Hat Pack Added
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Nunito', sans-serif",
            fontWeight: 700,
            fontSize: { xs: 36, sm: 48 },
            lineHeight: { xs: "44px", sm: "64px" },
            color: "#fff",
          }}
        >
          {hatPack.name}
        </Typography>
      </Stack>
      <Box sx={staggered(300, isActive)}>
        {hatPack.icon?.url ? (
          <Box
            component="img"
            src={hatPack.icon.url}
            alt=""
            sx={{ width: 168, height: 168, objectFit: "contain" }}
          />
        ) : (
          <Box
            sx={{
              width: 168,
              height: 168,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Iconify
              icon="solar:gift-bold"
              sx={{ width: 120, height: 120, color: "#fff" }}
            />
          </Box>
        )}
      </Box>
      {hatPack.description && (
        <Typography
          sx={{
            ...staggered(450, isActive),
            fontFamily: "'Nunito', sans-serif",
            fontWeight: 800,
            fontSize: { xs: 18, sm: 21 },
            lineHeight: 1,
            color: "#fff",
            maxWidth: 720,
          }}
        >
          {hatPack.description}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ ...staggered(600, isActive), width: "100%" }}
      >
        {isLast ? (
          <>
            <Button
              onClick={onAdvance}
              variant="outlined"
              color="primary"
              disableElevation
              sx={{ ...celebrationButtonSx, bgcolor: "#fff" }}
            >
              Close
            </Button>
            <Button
              onClick={goToHatPack}
              variant="contained"
              color="primary"
              disableElevation
              sx={celebrationButtonSx}
            >
              Go to Hat Pack
            </Button>
          </>
        ) : (
          <Button
            onClick={onAdvance}
            variant="contained"
            color="primary"
            disableElevation
            sx={{ ...celebrationButtonSx, minWidth: 160 }}
          >
            Next
          </Button>
        )}
      </Stack>
    </Box>
  );
};
